import { useEffect } from "react";

/**
 * Lightweight SEO head manager — sets <title>, meta tags, canonical, and JSON-LD
 * without an external dependency (no react-helmet).
 *
 * Props:
 *  title, description, image (absolute URL), url (absolute), type ('website'|'article')
 *  jsonLd: object or array of objects rendered as <script type="application/ld+json">
 */

function upsertMeta(attr, name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  if (!data) return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function SEOHead({
  title,
  description,
  image = "https://talkani.de/generated/hero.png",
  url,
  type = "website",
  lang = "de",
  jsonLd
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} — Talkani` : "Talkani — Logistik · Reinigung · Service";
    document.title = fullTitle;

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", "index, follow");
    document.documentElement.setAttribute("lang", lang);

    // Open Graph
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:url", url || window.location.href);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", "Talkani");
    upsertMeta("property", "og:locale", lang === "ar" ? "ar_AR" : lang === "en" ? "en_GB" : "de_DE");

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    // Canonical
    upsertLink("canonical", url || window.location.href.split("?")[0].split("#")[0]);

    if (Array.isArray(jsonLd)) {
      jsonLd.forEach((j, i) => upsertJsonLd(`ld-json-${i}`, j));
    } else if (jsonLd) {
      upsertJsonLd("ld-json-0", jsonLd);
    }
  }, [title, description, image, url, type, lang, jsonLd]);

  return null;
}
